/**
 * The page-level shaping controls of the budget consolidation: a filter over the contributing lines and
 * a fiscal-period window (PlanRollup-View-Shaping §9).
 *
 * **Page toolbar, not tab body — deliberately.** Both controls apply identically to every tab, and
 * putting them per tab is exactly what would make the tabs silently incomparable (§9.1). The grouping
 * chain is the opposite and lives in the tab that owns it.
 *
 * **Both narrowings live in one panel, and only their results live in the toolbar.** The period window
 * sits at the top of the panel because it is the coarser cut — "which months count" before "which lines
 * within them" — and because two always-visible period selects took toolbar room whether or not anyone
 * had used them. What stays on the toolbar is what is *active*: a chip per condition and one for the
 * period range, so the shaping is legible at a glance without opening anything, and each piece of it can
 * be removed where it is shown.
 *
 * The editor itself is the shipped `ConditionEditor`, wired the same way `RowAxisFilterRenderer` wires
 * it — over a `RowAxisFilterFieldDto[]` the server serves from the same registry that backs its own
 * evaluation, so the picker can never offer a field the read would have to drop (§7.1).
 */

import { lazy, Suspense, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Divider, MenuItem, Popover, Select, Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { CACHE_TIME } from '@/config/queryDefaults';
import { useFiscalPeriods } from '@/hooks/finance/useFiscalPeriod';
import {
  buildRowAxisContextSchema,
  resolveFilterFieldLabel,
  type RowAxisFilterFieldDto,
} from '@/finance/rowAxisFilterSchema';
import type { ConditionDto } from '@/types/core/Rules';
import {
  countConditions,
  listConditions,
  parseFilter,
  removeCondition,
  type RollupShaping,
} from './planRollupShaping';

// Same lazy load as the row-axis renderer: react-awesome-query-builder is a large chunk and the editor
// is only needed once a reader actually opens the panel.
const ConditionEditor = lazy(() =>
  import('@/core/rules/components/ConditionEditor').then((m) => ({ default: m.ConditionEditor }))
);

const FILTER_FIELDS_ENDPOINT = '/api/finance/planbudgetrollup/filter-fields';

/**
 * Room for a namespaced field name — "Account · Account number", "Cost Center · Name" — which the
 * editor's compact default truncates. Only this surface widens it: the row-axis renderer using the same
 * editor sits in a much narrower dialog over bare field names, and moving the shared default would push
 * its value input off the row.
 */
const FIELD_COLUMN_WIDTH = 280;

interface Props {
  value: RollupShaping;
  onChange: (next: RollupShaping) => void;
}

export function RollupShapingToolbar({ value, onChange }: Props) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: fiscalPeriods } = useFiscalPeriods();

  const { data: fields } = useQuery({
    queryKey: ['planRollupFilterFields'],
    queryFn: async () => (await apiClient.get<RowAxisFilterFieldDto[]>(FILTER_FIELDS_ENDPOINT)).data,
    staleTime: CACHE_TIME.METADATA,
  });

  // The picker's labels resolved before the schema is built, so the field list and the chips below
  // read identically -- two places deriving a display name separately is how they come to disagree.
  const localizedFields = useMemo(
    () => (fields ?? []).map((f) => ({ ...f, label: resolveFilterFieldLabel(t, f), groupLabel: undefined })),
    [fields, t],
  );

  // Null while loading, so the editor shows its own loading state rather than an empty field list — an
  // empty picker reads as "there is nothing to filter on here".
  const contextSchema = useMemo(
    () => (fields ? buildRowAxisContextSchema(localizedFields, 'PlanRollup') : null),
    [fields, localizedFields],
  );

  // A condition stores a field key and, for a dimension, an id — neither of which means anything to a
  // reader. The same catalogue that built the picker is what turns them back into words on the chips.
  const labelOf = useMemo(() => {
    const byField = new Map(localizedFields.map((f) => [f.field, f]));
    return (field: string, raw: unknown): { field: string; value: string } => {
      const meta = byField.get(field);
      const values = Array.isArray(raw) ? raw : [raw];
      const rendered = values
        .map((v) => meta?.enumValues?.find((o) => o.value === String(v))?.label ?? String(v ?? ''))
        .filter((s) => s.length > 0)
        .join(', ');
      return { field: meta?.label ?? field, value: rendered };
    };
  }, [localizedFields]);

  // Ordered by start date, not by period number: numbers restart every fiscal year, so ordering by them
  // interleaves the years — and the window itself is resolved on start date server-side (§8).
  const orderedPeriods = useMemo(
    () => [...(fiscalPeriods ?? [])].sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? '')),
    [fiscalPeriods],
  );

  // A period whose row is gone still has to render as something — its id beats a blank chip, which
  // would read as a range with no end rather than one pointing at a period that no longer exists (the
  // server warns about exactly that and drops the bound).
  const periodName = (id: string | undefined) =>
    id ? orderedPeriods.find((p) => p.id === id)?.name ?? id : undefined;

  const conditions = listConditions(value.filterJson);
  const conditionCount = countConditions(value.filterJson);
  const from = periodName(value.fromFiscalPeriodId);
  const to = periodName(value.toFiscalPeriodId);
  const hasPeriodRange = !!value.fromFiscalPeriodId || !!value.toFiscalPeriodId;
  // The panel owns both narrowings, so its button counts both — a range showing as nothing on a button
  // labelled "Filter" would under-report what is narrowing the page.
  const activeCount = conditionCount + (hasPeriodRange ? 1 : 0);

  const periodLabel = from && to
    ? t('Finance.PlanRollup.PeriodChipRange', { defaultValue: 'Period {{from}} – {{to}}', from, to })
    : from
      ? t('Finance.PlanRollup.PeriodChipFrom', { defaultValue: 'Period from {{from}}', from })
      : t('Finance.PlanRollup.PeriodChipTo', { defaultValue: 'Period until {{to}}', to });

  const setFilter = (condition: ConditionDto | null) => {
    const json = condition ? JSON.stringify(condition) : undefined;
    onChange({ ...value, filterJson: countConditions(json) > 0 ? json : undefined });
  };

  return (
    <>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Button
        size="small"
        variant={activeCount > 0 ? 'contained' : 'outlined'}
        startIcon={<FilterListIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        // Styled to be indistinguishable from the version picker beside it, because it sits in the same
        // toolbar row and MUI's defaults for the two components are not the same control: a small Button
        // is ~31px at 0.8125rem with a primary-tinted outline, a small Select is 40px at body1 with the
        // neutral input outline. Read off the theme rather than hardcoded so both modes hold — the dark
        // palette overrides text and divider tones, and a literal would only be right in one of them.
        //
        // The `contained` state is the exception and is deliberate: it is not decoration but the signal
        // that something is narrowing the page.
        sx={(theme) => ({
          textTransform: 'none',
          height: 40,
          fontSize: theme.typography.body1.fontSize,
          ...(activeCount > 0 ? {} : {
            color: theme.palette.text.primary,
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.23)'
              : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              borderColor: theme.palette.text.primary,
              backgroundColor: 'transparent',
            },
          }),
        })}
      >
        {/* `active`, not `count`: i18next treats `count` as the pluralisation option and resolves
            `key_one` / `key_other` before the key itself, so a plain resource entry would be a
            plural-form miss waiting to happen. Nothing here wants pluralising. */}
        {activeCount > 0
          ? t('Finance.PlanRollup.FilterWithCount', { defaultValue: 'Filter ({{active}})', active: activeCount })
          : t('Finance.PlanRollup.Filter', { defaultValue: 'Filter' })}
      </Button>

      {/* The period first, matching the panel's own order — coarse cut, then the conditions within it.
          Styled exactly like the condition chips beside it: both are one active narrowing that can be
          removed where it is shown, so a different colour would claim a difference in kind that is not
          there. What the period is gets said in its label, not in its styling. */}
      {hasPeriodRange && (
        <Chip
          size="small"
          label={periodLabel}
          onDelete={() => onChange({ ...value, fromFiscalPeriodId: undefined, toFiscalPeriodId: undefined })}
        />
      )}

      {conditions.map((rule, index) => {
        const { field, value: rendered } = labelOf(rule.field, rule.value);
        return (
          <Chip
            key={rule.nodeId ?? `${rule.field}-${index}`}
            size="small"
            label={`${field} ${rule.operator} ${rendered}`}
            onDelete={() => onChange({ ...value, filterJson: removeCondition(value.filterJson, rule.nodeId) })}
          />
        );
      })}

      {activeCount > 0 && (
        <Button size="small" onClick={() => onChange({})} sx={{ textTransform: 'none' }}>
          {t('Finance.PlanRollup.ClearShaping', { defaultValue: 'Clear' })}
        </Button>
      )}

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // Wide enough that a rule stays on ONE row. The editor declares its own minimum widths — field
        // (widened above), operator 112, value 300 — which with the row's gaps, padding and delete
        // button need ~860px inside the paper; below that the value wraps under the field and a single
        // condition reads as two. Capped against the viewport so a narrow window shrinks it instead of
        // pushing it off screen, and scrolls rather than growing past the fold once several rules exist.
        slotProps={{ paper: { sx: { p: 2, width: 900, maxWidth: '95vw', maxHeight: '70vh' } } }}
      >
        <Typography variant="subtitle2" gutterBottom>
          {t('Finance.PlanRollup.ShapingTitle', { defaultValue: 'Narrow what counts' })}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('Finance.PlanRollup.FilterHint', {
            defaultValue: 'Applies to every tab, so the tabs stay comparable. The consolidated total above stays unfiltered.',
          })}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="body2" sx={{ minWidth: 120 }}>
            {t('Finance.PlanRollup.PeriodRange', { defaultValue: 'Fiscal period' })}
          </Typography>
          <Select
            size="small"
            displayEmpty
            value={value.fromFiscalPeriodId ?? ''}
            onChange={(e) => onChange({ ...value, fromFiscalPeriodId: e.target.value || undefined })}
            sx={{ minWidth: 160 }}
            renderValue={(val) =>
              orderedPeriods.find((p) => p.id === val)?.name
              ?? t('Finance.PlanRollup.PeriodFrom', { defaultValue: 'From…' })}
          >
            <MenuItem value="">
              <em>{t('Finance.PlanRollup.PeriodAny', { defaultValue: 'No limit' })}</em>
            </MenuItem>
            {orderedPeriods.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
          <Typography variant="body2" color="text.secondary">–</Typography>
          <Select
            size="small"
            displayEmpty
            value={value.toFiscalPeriodId ?? ''}
            onChange={(e) => onChange({ ...value, toFiscalPeriodId: e.target.value || undefined })}
            sx={{ minWidth: 160 }}
            renderValue={(val) =>
              orderedPeriods.find((p) => p.id === val)?.name
              ?? t('Finance.PlanRollup.PeriodTo', { defaultValue: 'To…' })}
          >
            <MenuItem value="">
              <em>{t('Finance.PlanRollup.PeriodAny', { defaultValue: 'No limit' })}</em>
            </MenuItem>
            {orderedPeriods.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
          <Typography variant="caption" color="text.secondary">
            {t('Finance.PlanRollup.PeriodRangeHint', {
              defaultValue: 'Both ends inclusive; leave either open for no limit.',
            })}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('Finance.PlanRollup.FilterTitle', { defaultValue: 'Include only lines where…' })}
        </Typography>
        <Suspense fallback={<Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}>
          <ConditionEditor
            value={parseFilter(value.filterJson)}
            onChange={setFilter}
            contextSchema={contextSchema}
            readOnly={false}
            enableFieldComparison={false}
            extendedOperators
            compact
            fieldMinWidth={FIELD_COLUMN_WIDTH}
          />
        </Suspense>
      </Popover>
    </>
  );
}

export default RollupShapingToolbar;
